import React, { useState, useRef, useEffect } from 'react';
import { IoSearchOutline, IoLocationOutline, IoCloseOutline } from 'react-icons/io5';
import { useLanguage } from '../contexts/LanguageContext';
import './SearchBox.css';

// Force cache invalidation - updated with dynamic API calls

const SearchBox = ({ 
  onSearch, 
  onLocationSelect,
  placeholder,
  className = '',
  autoFocus = false 
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Debounced search for suggestions
  useEffect(() => {
    const fetchSuggestions = async (searchQuery) => {
      const suggestions = [];
      
      // If it looks like a zipcode
      if (/^\d{5}$/.test(searchQuery)) {
        suggestions.push({
          type: 'zipcode',
          text: searchQuery,
          description: t('zipcode')
        });
      }
      
      // If it contains letters, fetch cities from API
      if (/[a-zA-Z]/.test(searchQuery)) {
        try {
          const response = await fetch(`/api/cities/suggestions?q=${encodeURIComponent(searchQuery)}`);
          
          if (response.ok) {
            const citySuggestions = await response.json();
            
            citySuggestions.forEach(city => {
              suggestions.push({
                type: 'city',
                text: `${city.name}, ${city.state}`,
                description: t('city')
              });
            });
          }
        } catch (error) {
          console.error('Error fetching city suggestions:', error);
          // On error, continue with empty city suggestions
        }
      }
      
      setSuggestions(suggestions.slice(0, 8)); // Limit to 8 suggestions (matches backend)
      setShowSuggestions(suggestions.length > 0);
    };

    const timeoutId = setTimeout(() => {
      if (query.length >= 3) {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, t]); // Added 't' to dependencies since it's used inside fetchSuggestions

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setShowSuggestions(false);
    
    try {
      if (onSearch) {
        await onSearch(query.trim());
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    
    if (suggestion.type === 'zipcode') {
      // Search by zipcode using our API
      if (onSearch) {
        await onSearch(suggestion.text);
      }
    } else if (suggestion.type === 'city') {
      // Search by city using our API
      if (onSearch) {
        await onSearch(suggestion.text);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={`search-box ${className}`}>
      <div className="search-box__input-container">
        <IoSearchOutline className="search-box__search-icon" size={20} />
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setShowSuggestions(suggestions.length > 0)}
          placeholder={placeholder || t('searchPlaceholder')}
          className="search-box__input"
          disabled={loading}
        />
        
        {query && (
          <button
            onClick={clearSearch}
            className="search-box__clear-button"
            aria-label={t('clearSearch')}
          >
            <IoCloseOutline size={18} />
          </button>
        )}
        
        <button
          onClick={handleSearch}
          disabled={!query.trim() || loading}
          className="search-box__submit-button"
          aria-label={t('searchButton')}
        >
          {loading ? (
            <div className="search-box__spinner" />
          ) : (
            t('searchButton')
          )}
        </button>
      </div>
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="search-box__suggestions">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="search-box__suggestion"
            >
              <IoLocationOutline size={16} className="search-box__suggestion-icon" />
              <div className="search-box__suggestion-content">
                <span className="search-box__suggestion-text">{suggestion.text}</span>
                <span className="search-box__suggestion-type">{suggestion.description}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBox;